'use client';

import { useState } from 'react';

import { ButtonBase, Collapse, Container, Grid, List, ListItem, Stack, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';

import { servicesOverview } from '@/config/services-overview.config';
import colors from '@/styles/themes/colors';

import styles from './ServicesOverviewSection.module.scss';

const ServicesOverviewSection = () => {
  const t = useTranslations('home.servicesOverviewSection');
  const [activeItem, setActiveItem] = useState<{ column: string; item: string } | null>(null);

  const handleItemClick = (columnKey: string, itemKey: string) => {
    setActiveItem(prev => {
      if (prev && prev.column === columnKey && prev.item === itemKey) {
        return null;
      }

      return { column: columnKey, item: itemKey };
    });
  };

  return (
    <Container component="section" maxWidth="xl" className={styles.container}>
      <Stack className={styles.header}>
        <Typography variant="h3" component="h2" color={colors.white}>
          {t('title')}
        </Typography>
        <Typography variant="body1" color={colors.graytone300}>
          {t('description')}
        </Typography>
      </Stack>
      <Grid container spacing={{ xs: 6, md: 3 }} rowGap={6}>
        {servicesOverview.map(({ key, Icon, items }) => {
          const activeItemInColumn = activeItem?.column === key ? activeItem.item : null;
          const hasAnyActive = activeItem !== null;
          const isColumnMuted = hasAnyActive && activeItem?.column !== key;

          return (
            <Grid key={key} size={{ xs: 12, sm: 6, lg: 3 }}>
              <Stack className={styles.column}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                  <Icon size={16} muted={isColumnMuted} />
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    fontWeight={500}
                    color={isColumnMuted ? colors.graytone500 : colors.white}
                    className={styles.columnTitle}
                  >
                    {t(`columns.${key}.title`)}
                  </Typography>
                </Stack>
                <List disablePadding className={styles.list}>
                  {items.map(item => {
                    const isActive = activeItemInColumn === item;
                    const isMuted = hasAnyActive && !isActive;
                    const descriptionId = `services-overview-${key}-${item}-desc`;

                    return (
                      <ListItem
                        key={item}
                        disablePadding
                        disableGutters
                        sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                      >
                        <ButtonBase
                          component="button"
                          disableRipple
                          onClick={() => handleItemClick(key, item)}
                          aria-expanded={isActive}
                          aria-controls={descriptionId}
                          className={styles.item}
                          sx={{ color: isMuted ? colors.graytone500 : colors.white }}
                        >
                          {/* @ts-expect-error - Dynamic translation key from services config */}
                          {t(`columns.${key}.items.${item}.title`)}
                        </ButtonBase>
                        <Collapse in={isActive} id={descriptionId} timeout={300} unmountOnExit={false}>
                          <Typography variant="body1" color={colors.white} mt={1.5}>
                            {/* @ts-expect-error - Dynamic translation key from services config */}
                            {t(`columns.${key}.items.${item}.description`)}
                          </Typography>
                        </Collapse>
                      </ListItem>
                    );
                  })}
                </List>
              </Stack>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default ServicesOverviewSection;
